import { CustomFieldValue } from '../models/custom-fields'
import {
  SaleStatus,
  SERVICE_ORDERS_CUSTOM_FIELD,
  ServiceActions,
  ServiceFields,
  ServiceFinalStatus,
} from '../models/sale.constants'
import { ReduxCurrentSale } from '../state/slices/currentSale/currentSaleSlice'
import { isLegacyServiceSale } from './sale'

/**
 * Determines whether the service status is final.
 */
export const isServiceStatusFinal = (
  serviceFields?: ServiceFields
): boolean => {
  if (serviceFields?.status === undefined) {
    return false
  }
  return Object.values(ServiceFinalStatus).includes(
    (serviceFields.status as unknown) as ServiceFinalStatus
  )
}

/**
 * Determine whether this sale has a service order
 * If the sale in progress (Index-DB) contains the property
 * serviceFields.serviceAction with the values "CREATE_JOB" or "UPDATE_JOB",
 * or if the continued sale contains a field in the customFields
 * with the name "internal/sale-has-service-orders",
 * it is considered a sale WITH service.
 *
 * @return True if this sale contains a service, false otherwise
 */
export const hasServiceOrder = (
  serviceFields?: ServiceFields,
  customFields?: CustomFieldValue[]
) => {
  return (
    serviceFields?.serviceAction === ServiceActions.CREATE_JOB ||
    serviceFields?.serviceAction === ServiceActions.UPDATE_JOB ||
    customFields?.some(val => val.name === SERVICE_ORDERS_CUSTOM_FIELD)
  )
}

type HasServiceOrderWithAgendaParams = Pick<
  ReduxCurrentSale,
  'serviceFields' | 'customFields'
> & { status?: string | SaleStatus }
/**
 * Determine whether this sale has a service order that contains a service
 * agenda/schedule
 * @return True if this sale has the sale status 'service' and has a service
 * agenda, false otherwise
 */
export const hasServiceOrderWithAgenda = (
  sale: HasServiceOrderWithAgendaParams
) => {
  const { serviceFields, customFields, status } = sale
  const saleHasServiceOrder = hasServiceOrder(serviceFields, customFields)

  if (
    !(saleHasServiceOrder || isLegacyServiceSale(status)) ||
    !serviceFields?.serviceAgenda
  ) {
    return false
  }

  const { date, minutesScheduled } = serviceFields.serviceAgenda

  // Check if the required properties (date and minutesScheduled) exist and are valid
  return Boolean(date) && minutesScheduled > 0
}
